import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Alert,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import api from "../services/api";

// Helper function to flatten the hierarchy
const flattenHierarchy = (node) => {
  if (!node) {
    return [];
  }
  const list = [{ email: node.email, name: node.name }];
  if (node.reportees && node.reportees.length > 0) {
    for (const reportee of node.reportees) {
      list.push(...flattenHierarchy(reportee));
    }
  }
  return list;
};

const TodaySummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportees, setReportees] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const fetchReportees = async () => {
      try {
        const response = await api.get("users/get_reportees/", {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });

        const hierarchyData = response.data.hierarchy;
        if (hierarchyData) {
          const flatReporteeList = flattenHierarchy(hierarchyData);
          setReportees(flatReporteeList);
        } else {
          if (Array.isArray(response.data.hierarchy)) {
             const fullList = response.data.hierarchy.flatMap(head => flattenHierarchy(head));
             setReportees(fullList);
          } else {
             setReportees([]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch reportees:", err);
      }
    };

    fetchReportees();
  }, []);

  useEffect(() => {
    if (reportees.length > 0 && reportees[selectedIndex]) {
      fetchSummary(reportees[selectedIndex].email);
    } else if (reportees.length > 0 && selectedIndex === 0) {
        // Fallback for initial load if the first reportee is the one to show
        fetchSummary(reportees[0].email);
    } else {
      setLoading(false);
      setSummary(null);
    }
  }, [reportees, selectedIndex]);

  const fetchSummary = async (email) => {
    if (!email) return;

    try {
      setLoading(true);
      setSummary(null);
      setError(null);
      const response = await api.get(`timesheet/api/today/?email=${email}`);
      setSummary(response.data.today_summary);
    } catch (err) {
      setError(err.message || "Failed to fetch summary");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = (event) => {
    setSelectedIndex(event.target.value);
  };

  // +++ START OF FIX: Implemented the copy function +++
  const copyTableAsHtml = () => {
    if (!summary || Object.keys(summary).length === 0) return;

    let tableHtml = `<table border="1" style="border-collapse: collapse; width: 100%; font-family: sans-serif;">
        <thead>
            <tr>
                <th style="padding: 8px; text-align: left; background-color: #f2f2f2;">Category</th>
                <th style="padding: 8px; text-align: left; background-color: #f2f2f2;">Task Description</th>
                <th style="padding: 8px; text-align: right; background-color: #f2f2f2;">Time (minutes)</th>
            </tr>
        </thead>
        <tbody>`;

    Object.entries(summary).forEach(([category, tasks]) => {
      Object.entries(tasks).forEach(([task, time], index) => {
        tableHtml += `<tr>
            <td style="padding: 8px;">${index === 0 ? category : ""}</td>
            <td style="padding: 8px;">${task}</td>
            <td style="padding: 8px; text-align: right;">${time}</td>
        </tr>`;
      });
    });

    tableHtml += `</tbody></table>`;

    const type = "text/html";
    const blob = new Blob([tableHtml], { type });
    const clipboardItem = new ClipboardItem({ [type]: blob });

    navigator.clipboard.write([clipboardItem]).then(
      () => {
        alert("Summary copied to clipboard with table formatting!");
      },
      (err) => {
        console.error("Error copying table as HTML: ", err);
        alert("Failed to copy table.");
      }
    );
  };
  // +++ END OF FIX +++

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Paper elevation={3} style={{ padding: "24px", maxWidth: "800px", margin: "20px auto" }}>
      <Typography variant="h4" gutterBottom>
        Today's Summary
      </Typography>

      <FormControl fullWidth variant="outlined" style={{ marginBottom: "20px" }}>
        <InputLabel>Reportee</InputLabel>
        <Select value={selectedIndex} onChange={handleSelectionChange} label="Reportee">
          {reportees.map((reportee, index) => (
            <MenuItem key={reportee.email} value={index}>
              {reportee.name} ({reportee.email})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <IconButton
        onClick={copyTableAsHtml}
        color="primary"
        aria-label="copy table"
        style={{ marginBottom: "10px" }}
      >
        <ContentCopy />
      </IconButton>

      {!summary || Object.keys(summary).length === 0 ? (
        <Alert severity="info">No summary available for today.</Alert>
      ) : (
        // +++ START OF FIX: Implemented the table rendering logic +++
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="summary table">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Task Description</TableCell>
                <TableCell align="right" style={{ fontWeight: 'bold' }}>Time (minutes)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(summary).flatMap(([category, tasks]) =>
                Object.entries(tasks).map(([task, time], index) => (
                  <TableRow key={`${category}-${task}`}>
                    {/* Show category only for the first row of that category group */}
                    <TableCell component="th" scope="row">
                      {index === 0 ? category : ""}
                    </TableCell>
                    <TableCell>{task}</TableCell>
                    <TableCell align="right">{time}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        // +++ END OF FIX +++
      )}
    </Paper>
  );
};

export default TodaySummary;